import React, { useEffect, useState } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import { CalendarMonthOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { Primary, Secondary } from './Buttons';
import CustomLabel from './CustomLabel';
import CustomValidationText from './CustomValidationText';

/**
 * Trigger that looks like the old rsuite DateRangePicker input, but opens a
 * dialog with two separate date pickers (from / to) instead of a range calendar.
 *
 * value / onChange speak the same shape the APIs already use:
 *   { from_date: ISOString, to_date: ISOString } | null
 */
const DateRangeDialogPicker = ({
  value = null,
  onChange,
  placeholder = 'Filter by date range',
  label,
  labelStyle = {},
  // moment format used for both the trigger text and the pickers
  displayFormat = 'DD-MMM-YYYY',
  // 'day' | 'month' — month snaps both dates to the 1st and hides the day view
  granularity = 'day',
  disabled = false,
  error,
  minDate,
  maxDate,
  dialogTitle = 'Select date range',
  borderColor,
  sx = {},
  fromLabel = 'From',
  toLabel = 'To',
  // Optional controlled mode: a caller that already has its own control (the Reports
  // Filter icon) can own the open state and hide this component's input-style trigger,
  // so the user gets ONE dialog instead of a popover that opens a second dialog.
  open: openProp,
  onOpenChange,
  hideTrigger = false,
}) => {
  const theme = useTheme();
  const isMonth = granularity === 'month';

  const [openState, setOpenState] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openState;
  const setOpen = (next) => {
    if (!isControlled) setOpenState(next);
    onOpenChange?.(next);
  };
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const parsed = {
    from: value?.from_date ? moment(value.from_date) : null,
    to: value?.to_date ? moment(value.to_date) : null,
  };

  const hasValue = Boolean(parsed.from?.isValid() && parsed.to?.isValid());

  // keep the dialog's draft in sync with whatever is committed when it opens
  useEffect(() => {
    if (open) {
      setFromDate(hasValue ? parsed.from : null);
      setToDate(hasValue ? parsed.to : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const normalize = (date) => (isMonth ? moment(date).startOf('month') : moment(date));

  // The pickers still carry a day/time, so a raw from/to can't be used as a
  // bound directly. In month mode the two sides must land on different months,
  // so each bound is pushed a whole month clear of the other side.
  const compareUnit = isMonth ? 'month' : 'day';
  // earliest the "to" side may be, given "from"
  const lowerBound = (date) =>
    date ? (isMonth ? moment(date).startOf('month').add(1, 'month') : moment(date).startOf('day')) : null;
  // latest the "from" side may be, given "to"
  const upperBound = (date) =>
    date ? (isMonth ? moment(date).startOf('month').subtract(1, 'month').endOf('month') : moment(date).endOf('day')) : null;

  const handleApply = () => {
    if (!fromDate || !toDate) return;
    onChange?.({
      from_date: normalize(fromDate).toISOString(),
      to_date: normalize(toDate).toISOString(),
    });
    setOpen(false);
  };

  const handleClear = (e) => {
    e?.stopPropagation?.();
    setFromDate(null);
    setToDate(null);
    onChange?.(null);
    setOpen(false);
  };

  const triggerBorder = borderColor || '#E4E7EC';

  const pickerViews = isMonth ? ['year', 'month'] : ['year', 'month', 'day'];
  const pickerSlotProps = {
    textField: {
      fullWidth: true,
      placeholder: displayFormat,
      sx: {
        '& .MuiOutlinedInput-root': { borderRadius: '10px', background: theme.palette.customColors.whiteBG },
      },
    },
  };

  return (
    <Box sx={{ width: '100%', display: hideTrigger ? 'none' : 'block', ...sx }}>
      {label && <CustomLabel sx={{ ...labelStyle }}>{label}</CustomLabel>}

      {!hideTrigger && <Box
        onClick={() => !disabled && setOpen(true)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          width: '100%',
          minHeight: '48px',
          paddingInline: '15px',
          paddingBlock: '10px',
          border: `1px solid ${triggerBorder}`,
          borderRadius: '10px',
          background: theme.palette.customColors.whiteBG,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          overflow: 'hidden',
          ...sx,
        }}
      >
        <Typography
          noWrap
          sx={{
            fontSize: '1rem',
            color: hasValue ? theme.palette.customColors.g300 : theme.palette.customColors.g100,
          }}
        >
          {hasValue
            ? `${parsed.from.format(displayFormat)} - ${parsed.to.format(displayFormat)}`
            : placeholder}
        </Typography>

        {hasValue && !disabled ? (
          <IconButton
            size="small"
            aria-label="clear date range"
            onClick={handleClear}
            sx={{ padding: '2px', color: theme.palette.customColors.g100 }}
          >
            <Close sx={{ fontSize: '1.15rem' }} />
          </IconButton>
        ) : (
          <CalendarMonthOutlined sx={{ fontSize: '1.15rem', color: theme.palette.customColors.g100 }} />
        )}
      </Box>}

      {error && <CustomValidationText value={error} />}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            fontWeight: 700,
            fontSize: '1.25rem',
            color: theme.palette.customColors.g300,
          }}
        >
          {dialogTitle}
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{ padding: '4px', color: theme.palette.customColors.g100 }}
          >
            <Close sx={{ fontSize: '1.35rem' }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ paddingBlock: '0.5rem !important' }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Grid container spacing={2} paddingTop={"0.5rem"}>
              <Grid item xs={12} sm={6}>
                <CustomLabel sx={{ textTransform: 'none' }}>{fromLabel}</CustomLabel>
                <DatePicker
                  views={pickerViews}
                  openTo={isMonth ? 'month' : 'day'}
                  format={displayFormat}
                  value={fromDate}
                  minDate={minDate ? moment(minDate) : undefined}
                  maxDate={upperBound(toDate) || (maxDate ? moment(maxDate) : undefined)}
                  onChange={(newValue) => {
                    setFromDate(newValue);
                    // a from-date that no longer sits strictly before the to-date invalidates it
                    const clashes = isMonth
                      ? moment(newValue).isSameOrAfter(toDate, compareUnit)
                      : moment(newValue).isAfter(toDate, compareUnit);
                    if (newValue && toDate && clashes) setToDate(null);
                  }}
                  slotProps={pickerSlotProps}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomLabel sx={{ textTransform: 'none' }}>{toLabel}</CustomLabel>
                <DatePicker
                  views={pickerViews}
                  openTo={isMonth ? 'month' : 'day'}
                  format={displayFormat}
                  value={toDate}
                  minDate={lowerBound(fromDate) || (minDate ? moment(minDate) : undefined)}
                  maxDate={maxDate ? moment(maxDate) : undefined}
                  onChange={(newValue) => setToDate(newValue)}
                  slotProps={pickerSlotProps}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ padding: '1rem 1.5rem', justifyContent: 'flex-end', gap: 1 }}>
          <Secondary
            onClick={handleClear}
            sx={{ padding: '0.5rem 1.25rem', fontSize: '0.95rem' }}
          >
            Clear
          </Secondary>
          <Primary
            onClick={handleApply}
            disabled={!fromDate || !toDate || !moment(fromDate).isValid() || !moment(toDate).isValid()}
            sx={{ padding: '0.5rem 1.5rem', fontSize: '0.95rem', minWidth: 'unset' }}
          >
            Apply
          </Primary>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DateRangeDialogPicker;
